import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { PieChart as MuiPieChart } from '@mui/x-charts/PieChart';
import KpiCard from './KpiCard';
import { getUncatchEndpointData } from '../utils/request';
import {
  VDR_CHART,
  vdrChartSx,
  vdrLegendLabelStyle,
  vdrOverlaySlotProps,
  vdrTooltipPopperSx
} from './vdrChartStyle';

type AnatomiaPayload = {
  contadores: {
    sseePoder: number;
    transformadoresDx: number;
    postes: number;
    nodos: number;
    tramos: number;
  };
  donutTension: {
    kmBt: number;
    kmMt: number;
    kmSinClasificar: number;
  };
  donutTensionDetallado: {
    tensiones: Array<{ tension: number; km: number }>;
    kmSinClasificar: number;
  };
  donutDensidad: {
    densidades: Array<{ densidad: string; count: number }>;
  };
};

const PAYLOAD_INICIAL: AnatomiaPayload = {
  contadores: {
    sseePoder: 0,
    transformadoresDx: 0,
    postes: 0,
    nodos: 0,
    tramos: 0
  },
  donutTension: { kmBt: 0, kmMt: 0, kmSinClasificar: 0 },
  donutTensionDetallado: { tensiones: [], kmSinClasificar: 0 },
  donutDensidad: { densidades: [] }
};

const formatearKm = (v: number) =>
  `${v.toLocaleString('es-CL', { maximumFractionDigits: 0 })} km`;

const formatearTension = (kV: number) => {
  // BD almacena tensiones en kV (p. ej. 0.32, 0.38, 12, 13.2, 23). Mostramos en kV
  // con uno o dos decimales según convenga.
  const decimales = kV < 1 ? 2 : kV % 1 === 0 ? 0 : 1;
  return `${kV.toLocaleString('es-CL', {
    maximumFractionDigits: decimales,
    minimumFractionDigits: decimales
  })} kV`;
};

// BT y MT con la convención de color de la plataforma (BT azul #0072b2, MT naranja #e06c00,
// como en los estudios de conexión). En el donut detallado cada grupo es una rampa de un solo
// tono, de claro a oscuro a medida que sube la tensión (el índice 1 es el color base).
const COLOR_BT = '#0072b2';
const COLOR_MT = '#e06c00';
const COLORES_BT = ['#3b96d9', COLOR_BT, '#025485', '#013b5f'];
const COLORES_MT = ['#ff9047', COLOR_MT, '#b85803', '#964601', '#763500', '#5a2700'];
const COLOR_SIN_CLASIFICAR = VDR_CHART.lineStrong;

const colorParaTension = (tension: number, idxEnGrupo: number): string => {
  if (tension > 0 && tension < 1) {
    return COLORES_BT[idxEnGrupo % COLORES_BT.length];
  }
  if (tension >= 1) {
    return COLORES_MT[idxEnGrupo % COLORES_MT.length];
  }
  return COLOR_SIN_CLASIFICAR;
};

// Donut de densidad de alimentadores. Valores canónicos del enum NORM-006:
// 'alta' | 'media' | 'baja' | 'muy_baja' (ver backend/typings/estudioConexionGD.ts).
// 'sin_clasificar' representa NULL/vacío en BD.
const ETIQUETAS_DENSIDAD: Record<string, string> = {
  alta: 'Alta',
  media: 'Media',
  baja: 'Baja',
  muy_baja: 'Muy baja',
  sin_clasificar: 'Sin clasificar'
};

// La densidad es una escala ordenada (no un semáforo): rampa de marca de oscuro (alta) a claro.
const COLORES_DENSIDAD: Record<string, string> = {
  alta: VDR_CHART.ink4,
  media: VDR_CHART.cyanDeep,
  baja: '#068aae',
  muy_baja: VDR_CHART.cyan,
  sin_clasificar: COLOR_SIN_CLASIFICAR
};

// Orden visual del donut: de mayor a menor densidad, sin clasificar al final.
const ORDEN_DENSIDAD = ['alta', 'media', 'baja', 'muy_baja', 'sin_clasificar'];

const AnatomiaModelo: FunctionComponent = () => {
  const [payload, setPayload] = useState<AnatomiaPayload>(PAYLOAD_INICIAL);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    const fetchData = async () => {
      try {
        const res = await getUncatchEndpointData({
          endpoint: 'penetracion/anatomia'
        });
        if (cancelado) return;
        setPayload(res as AnatomiaPayload);
        setError(null);
      } catch (err) {
        if (cancelado) return;
        console.error(err);
        setError('No se pudo cargar la anatomía del modelo.');
      }
    };
    fetchData();
    return () => {
      cancelado = true;
    };
  }, []);

  const dataDonutTension = useMemo(() => {
    const items = [
      {
        id: 0,
        label: 'Baja tensión',
        value: payload.donutTension.kmBt,
        color: COLOR_BT
      },
      {
        id: 1,
        label: 'Media tensión',
        value: payload.donutTension.kmMt,
        color: COLOR_MT
      },
      {
        id: 2,
        label: 'Sin clasificar',
        value: payload.donutTension.kmSinClasificar,
        color: COLOR_SIN_CLASIFICAR
      }
    ];
    return items.filter((it) => it.value > 0);
  }, [payload]);

  const dataDonutTensionDetallada = useMemo(() => {
    // Agrupamos tensiones nominalmente equivalentes (mismo nivel BT estándar)
    // para reducir slices pequeñas y dar lectura más limpia:
    //   0,22 + 0,23 → se muestra como 0,22 kV
    //   0,38 + 0,40 → se muestra como 0,38 kV
    // El resto de tensiones queda como slice individual.
    const GRUPOS: Array<{ miembros: number[]; label: string }> = [
      { miembros: [0.22, 0.23], label: '0,22 kV' },
      { miembros: [0.38, 0.4], label: '0,38 kV' }
    ];
    const aproxIgual = (a: number, b: number) => Math.abs(a - b) < 0.001;
    const grupoDe = (t: number) =>
      GRUPOS.find((g) => g.miembros.some((m) => aproxIgual(m, t)));

    type Acumulado = { km: number; tensionRef: number; label: string };
    const acumulado = new Map<string, Acumulado>();

    for (const t of payload.donutTensionDetallado.tensiones) {
      const grupo = grupoDe(t.tension);
      const clave = grupo ? grupo.label : formatearTension(t.tension);
      const label = grupo ? grupo.label : formatearTension(t.tension);
      // Para el color usamos el mínimo del grupo (todos los miembros caen en
      // la misma categoría BT/MT, así que el resultado es consistente).
      const tensionRef = grupo ? Math.min(...grupo.miembros) : t.tension;
      const prev = acumulado.get(clave);
      if (prev) {
        prev.km += t.km;
      } else {
        acumulado.set(clave, { km: t.km, tensionRef, label });
      }
    }

    const agrupado = Array.from(acumulado.values()).sort(
      (a, b) => a.tensionRef - b.tensionRef
    );

    let idxBt = 0;
    let idxMt = 0;
    const items = agrupado.map((g, i) => {
      const esBt = g.tensionRef > 0 && g.tensionRef < 1;
      const idxEnGrupo = esBt ? idxBt++ : idxMt++;
      return {
        id: i,
        label: g.label,
        value: g.km,
        color: colorParaTension(g.tensionRef, idxEnGrupo)
      };
    });

    if (payload.donutTensionDetallado.kmSinClasificar > 0) {
      items.push({
        id: items.length,
        label: 'Sin clasificar',
        value: payload.donutTensionDetallado.kmSinClasificar,
        color: COLOR_SIN_CLASIFICAR
      });
    }
    return items.filter((it) => it.value > 0);
  }, [payload]);

  const dataDonutDensidad = useMemo(() => {
    return payload.donutDensidad.densidades
      .map((d, i) => ({
        id: i,
        key: d.densidad,
        label: ETIQUETAS_DENSIDAD[d.densidad] ?? d.densidad,
        value: d.count,
        color: COLORES_DENSIDAD[d.densidad] ?? COLOR_SIN_CLASIFICAR
      }))
      .filter((it) => it.value > 0)
      .sort((a, b) => {
        const aIdx = ORDEN_DENSIDAD.indexOf(a.key);
        const bIdx = ORDEN_DENSIDAD.indexOf(b.key);
        return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
      });
  }, [payload]);

  if (error) {
    return <div className='kpi-card-status kpi-card-status--error'>{error}</div>;
  }

  return (
    <div className='bloque1-anatomia'>
      <div className='bloque1-anatomia-row'>
        <KpiCard
          valor={payload.contadores.sseePoder}
          label='Subestaciones de poder'
          variante='compacto'
        />
        <KpiCard
          valor={payload.contadores.transformadoresDx}
          label='Transformadores de distribución'
          variante='compacto'
        />
        <KpiCard
          valor={payload.contadores.postes}
          label='Postes modelados'
          variante='compacto'
        />
        <KpiCard
          valor={payload.contadores.nodos}
          label='Nodos eléctricos'
          variante='compacto'
        />
        <KpiCard
          valor={payload.contadores.tramos}
          label='Tramos de línea'
          variante='compacto'
        />
      </div>

      <div className='bloque1-donuts-row'>
        <div className='bloque1-donut'>
          <h5 className='bloque1-donut-titulo'>Red por tensión</h5>
          {dataDonutTension.length > 0 ? (
            <MuiPieChart
              series={[
                {
                  data: dataDonutTension,
                  innerRadius: 50,
                  outerRadius: 85,
                  paddingAngle: 2,
                  cornerRadius: 0,
                  valueFormatter: (item) => formatearKm(item.value),
                  highlightScope: { fade: 'global', highlight: 'item' }
                }
              ]}
              height={260}
              margin={{ top: 8, bottom: 80, left: 8, right: 8 }}
              sx={vdrChartSx}
              slotProps={{
                legend: {
                  direction: 'row',
                  position: { vertical: 'bottom', horizontal: 'middle' },
                  padding: { top: 18, bottom: 4, left: 8, right: 8 },
                  itemGap: 12,
                  markGap: 6,
                  itemMarkWidth: 10,
                  itemMarkHeight: 10,
                  labelStyle: { ...vdrLegendLabelStyle, fontSize: 11.5 }
                },
                popper: { sx: vdrTooltipPopperSx },
                ...vdrOverlaySlotProps
              }}
            />
          ) : (
            <div className='bloque1-donut-vacio'>Sin datos de tensión.</div>
          )}
        </div>

        <div className='bloque1-donut'>
          <h5 className='bloque1-donut-titulo'>Red por nivel de tensión exacto</h5>
          {dataDonutTensionDetallada.length > 0 ? (
            <MuiPieChart
              series={[
                {
                  data: dataDonutTensionDetallada,
                  innerRadius: 50,
                  outerRadius: 85,
                  paddingAngle: 2,
                  cornerRadius: 0,
                  valueFormatter: (item) => formatearKm(item.value),
                  highlightScope: { fade: 'global', highlight: 'item' }
                }
              ]}
              height={260}
              margin={{ top: 8, bottom: 80, left: 8, right: 8 }}
              sx={vdrChartSx}
              slotProps={{
                legend: {
                  direction: 'row',
                  position: { vertical: 'bottom', horizontal: 'middle' },
                  padding: { top: 18, bottom: 4, left: 8, right: 8 },
                  itemGap: 12,
                  markGap: 6,
                  itemMarkWidth: 10,
                  itemMarkHeight: 10,
                  labelStyle: { ...vdrLegendLabelStyle, fontSize: 11.5 }
                },
                popper: { sx: vdrTooltipPopperSx },
                ...vdrOverlaySlotProps
              }}
            />
          ) : (
            <div className='bloque1-donut-vacio'>Sin datos de tensión detallada.</div>
          )}
        </div>

        <div className='bloque1-donut'>
          <h5 className='bloque1-donut-titulo'>Alimentadores por densidad</h5>
          {dataDonutDensidad.length > 0 ? (
            <MuiPieChart
              series={[
                {
                  data: dataDonutDensidad,
                  innerRadius: 50,
                  outerRadius: 85,
                  paddingAngle: 2,
                  cornerRadius: 0,
                  valueFormatter: (item) =>
                    `${item.value.toLocaleString('es-CL')} alim.`,
                  highlightScope: { fade: 'global', highlight: 'item' }
                }
              ]}
              height={260}
              margin={{ top: 8, bottom: 80, left: 8, right: 8 }}
              sx={vdrChartSx}
              slotProps={{
                legend: {
                  direction: 'row',
                  position: { vertical: 'bottom', horizontal: 'middle' },
                  padding: { top: 18, bottom: 4, left: 8, right: 8 },
                  itemGap: 12,
                  markGap: 6,
                  itemMarkWidth: 10,
                  itemMarkHeight: 10,
                  labelStyle: { ...vdrLegendLabelStyle, fontSize: 11.5 }
                },
                popper: { sx: vdrTooltipPopperSx },
                ...vdrOverlaySlotProps
              }}
            />
          ) : (
            <div className='bloque1-donut-vacio'>
              Sin datos de densidad de alimentadores.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnatomiaModelo;
